import React, { useEffect, useState } from 'react';
import { GlobalStyles } from './styles/GlobalStyles';
import { AdminApp, MainContent, PageContent } from './styles/AppStyles';
import Dashboard from './pages/Dashboard/Dashboard';
import Login from './pages/Auth/Login';
import UserRegister from './pages/Auth/Register';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Users from './pages/Users/Users';
import Products from './pages/Products/Products';
import Orders from './pages/Orders/Orders';
import Analytics from './pages/Analytics/Analytics';
import Settings from './pages/Settings/Settings';
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Access } from './pages/Access/Access';
import PublicRoute from './common/PublicRoute';
import ProtectedRoute from './common/ProtectedRoute';
import Category from './pages/Category/Category';
import SubCategory from './pages/Category/SubCategory';
import { useDispatch, useSelector } from 'react-redux';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Banner from './pages/Banner/banner';
import { menuItems } from './common/common';
import VendorForm from './pages/Vendor/VendorForm';
import { VendorList } from './pages/Vendor/VendorList';
import { VendorManage } from './pages/Vendor/VendorManage';
import EditProduct from './pages/Products/EditProduct';
import './index.css';
import BusinessForm from './pages/Vendor/Documents';
import ViewProduct from './pages/Products/ViewProduct';
import { AddProduct } from './pages/Products/AddProduct';
import BusinessSetupForm from './pages/Vendor/Documents';
import DocsPending from './pages/Vendor/DocsPending';
import { MyAccounts } from './common/CommonApi';
import { loginSuccess } from './store/slices/auth';
import VendorDetails from './pages/Vendor/VendorDetails';
import ViewOrder from './pages/Orders/OrdersView';
import StatusOrder from './pages/Orders/OrdersStatus';
import SubSubCategory from './pages/Category/SubSubCategory';
import HomeApp from './pages/HomeApp/HomeApp';
import OperationsManage from "./pages/Operations/operation";
import ViewApp from './pages/HomeApp/ViewAndAdd';
// import Profile from './pages/Profile/ViewProfile';
import OperationList from './pages/Operations/operationList';
import BrandsPage from './pages/Brands/BrandsList';
import Coupons from './pages/Coupons/Coupons';

const NotFound = () => {
  return (
    <div style={{ padding: "40px", textAlign: "center" }}>
      <h2>404 - Page Not Found</h2>
      <p>The page you are looking for does not exist.</p>
    </div>
  );
};

const AdminLayout = ({ children }) => {
  const dispatch = useDispatch();
  const componentStores = useSelector((state) => state.component);
  const auth = useSelector((state) => state.auth);
  const location = useLocation();
  const [isDocsResolved, setIsDocsResolved] = useState(false);
  const [docView, setDocView] = useState("dashboard");

  useEffect(() => {
    let mounted = true;

    const resolveDocView = (isDocs, isDocsPending) => {
      if (isDocs) return "dashboard";
      if (isDocsPending) return "pending";
      return "setup";
    };

    const handleAccount = async () => {
      if (!auth?.token) {
        if (mounted) {
          setDocView("dashboard");
          setIsDocsResolved(true);
        }
        return;
      }

      if (
        typeof auth?.isDocs === "boolean" &&
        typeof auth?.isDocsPending === "boolean"
      ) {
        if (mounted) {
          setDocView(resolveDocView(auth.isDocs, auth.isDocsPending));
          setIsDocsResolved(true);
        }
        return;
      }

      try {
        const res = await MyAccounts();
        const resolvedIsDocs = Boolean(res?.isDocs);
        const resolvedIsDocsPending = Boolean(res?.isDocsPending);

        if (mounted) {
          setDocView(resolveDocView(resolvedIsDocs, resolvedIsDocsPending));
          dispatch(
            loginSuccess({
              token: auth.token,
              refreshToken: auth.refreshToken,
              _id: res?._id || auth._id,
              permissions: res?.permissions || auth.permissions,
              email: res?.email || auth.email,
              fullname: res?.fullname || auth.fullname,
              isDocs: resolvedIsDocs,
              isDocsPending: resolvedIsDocsPending,
            })
          );
        }
      } catch (error) {
        if (mounted) {
          setDocView("dashboard");
        }
      } finally {
        if (mounted) {
          setIsDocsResolved(true);
        }
      }
    };

    handleAccount();
    return () => {
      mounted = false;
    };
  }, [auth?.token, auth?.isDocs, auth?.isDocsPending, dispatch]);

  if (!isDocsResolved) {
    return (
      <div style={{ textAlign: "center", marginTop: "24px" }}>
        Loading...
      </div>
    );
  }

  if (docView === "pending") {
    return <DocsPending />;
  }

  if (docView === "setup") {
    return <BusinessSetupForm />;
  }

  const pathToKeyMap = menuItems.reduce((map, item) => {
    map[item.to] = item.id;
    return map;
  }, {});
  const activePage = pathToKeyMap[location.pathname] || 'dashboard';
  return (
    <>
      <AdminApp>
        <Sidebar
          collapsed={componentStores.isSideBar}
        />
        <MainContent className={componentStores.isSideBar ? 'sidebar-collapsed' : ''}>
          {
            componentStores.isHeaderBar &&
            <Header
              activePage={activePage}
              sidebarCollapsed={componentStores.isSideBar}
            />
          }
          <PageContent>
            {children}
          </PageContent>
        </MainContent>
      </AdminApp>
    </>
  );
};

function App() {
  // console.log("--->admin")
  return (
    <>
      <GlobalStyles />
      <Router>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* Public routes */}
          <Route
            path="/login"
            element={<PublicRoute><Login /></PublicRoute>}
          />
          <Route
            path="/signup"
            element={<PublicRoute><UserRegister /></PublicRoute>}
          />
          <Route
            path="/partner-form"
            element={<PublicRoute><VendorForm /></PublicRoute>}
          />

          {/* Private routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requiredPermission="Dashboard">
                <AdminLayout><Dashboard /></AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/users"
            element={
              <ProtectedRoute requiredPermission="Users">
                <AdminLayout><Users /></AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/products"
            element={
              <ProtectedRoute requiredPermission="Products">
                <AdminLayout><Products /></AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/add"
            element={
              <ProtectedRoute requiredPermission="Products">
                <AdminLayout><AddProduct /></AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/edit"
            element={
              <ProtectedRoute requiredPermission="Products">
                <AdminLayout><EditProduct /></AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/view"
            element={
              <ProtectedRoute requiredPermission="Products">
                <AdminLayout><ViewProduct /></AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/category"
            element={
              <ProtectedRoute requiredPermission="Category">
                <AdminLayout><Category /></AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/banners"
            element={
              <ProtectedRoute requiredPermission="Banners">
                <AdminLayout><Banner /></AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/category/sub-category"
            element={
              <ProtectedRoute requiredPermission="Category">
                <AdminLayout><SubCategory /></AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/category/sub-category/ss-category"
            element={
              <ProtectedRoute requiredPermission="Category">
                <AdminLayout><SubSubCategory /></AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders"
            element={
              <ProtectedRoute requiredPermission="Orders">
                <AdminLayout><Orders /></AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders/view"
            element={
              <ProtectedRoute requiredPermission="Orders">
                <AdminLayout><ViewOrder /></AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/status"
            element={
              <ProtectedRoute requiredPermission="Orders">
                <AdminLayout><StatusOrder /></AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/analytics"
            element={
              <ProtectedRoute requiredPermission="Analytics">
                <AdminLayout><Analytics /></AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute requiredPermission="Settings">
                <AdminLayout><Settings /></AdminLayout>
              </ProtectedRoute>
            }
          />
          {/* <Route
            path="/profile"
            element={
              <ProtectedRoute requiredPermission="Analytics">
                <AdminLayout><Profile /></AdminLayout>
              </ProtectedRoute>
            }
          /> */}

          <Route
            path="/access"
            element={
              <ProtectedRoute requiredPermission="Access">
                <AdminLayout><Access /></AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor"
            element={
              <ProtectedRoute requiredPermission="Vendor">
                <AdminLayout><VendorList /></AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brands"
            element={
              <ProtectedRoute requiredPermission="Brands">
                <AdminLayout><BrandsPage /></AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/coupons"
            element={
              <ProtectedRoute requiredPermission="Coupons">
                <AdminLayout><Coupons /></AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/view"
            element={
              <ProtectedRoute requiredPermission="Vendor">
                <AdminLayout><VendorDetails /></AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/operation"
            element={
              <ProtectedRoute requiredPermission="Vendor">
                <AdminLayout><OperationsManage /></AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/operation/brands"
            element={
              <ProtectedRoute requiredPermission="HomeApps">
                <AdminLayout><OperationList /></AdminLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/vendor/manage"
            element={
              <ProtectedRoute requiredPermission="Vendor">
                <AdminLayout><VendorManage /></AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/home"
            element={
              <ProtectedRoute requiredPermission="HomeApps">
                <AdminLayout><HomeApp /></AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/home/category"
            element={
              <ProtectedRoute requiredPermission="HomeApps">
                <AdminLayout><ViewApp /></AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="*"
            element={
              <ProtectedRoute requiredPermission="HomeApps">
                <AdminLayout><NotFound /></AdminLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
}

export default App;
